/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { isElementNull } from '../../../../utils';
import { CustomMarkerLoaderService } from '../custom-marker-loader';
import { IGoogleMapsLoaderService } from './google-maps-loader.service.interface';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsLoaderService implements IGoogleMapsLoaderService {
  public googleMapsLoaded$: Observable<boolean>;
  public customMarkerLoaded$: Observable<boolean>;
  public positionHighlightOverlayLoaded$: Observable<boolean>;
  public infoBoxLoaded$: Observable<boolean>;

  private readonly _googleMapsLoadedSubject = new BehaviorSubject<boolean>(false);
  private readonly _customMarkerLoadedSubject = new BehaviorSubject<boolean>(false);
  private readonly _positionHighlightOverlayLoadedSubject = new BehaviorSubject<boolean>(false);
  private readonly _infoBoxLoadedSubject = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: object,
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _customMarkerLoaderService: CustomMarkerLoaderService
  ) {
    this.googleMapsLoaded$ = this._googleMapsLoadedSubject.asObservable();
    this.customMarkerLoaded$ = this._customMarkerLoadedSubject.asObservable();
    this.positionHighlightOverlayLoaded$ = this._positionHighlightOverlayLoadedSubject.asObservable();
    this.infoBoxLoaded$ = this._infoBoxLoadedSubject.asObservable();
  }

  public loadMapOnly(apiKey: string, apiVersion: string): void {
    if (isPlatformServer(this._platformId) || this.isGoogleMapsLoaded()) {
      return;
    }

    // Detect if browser is IE11 and set api version to 3.47 (last supported version for IE)
    if (window.navigator.userAgent.indexOf('Trident/') > 0) {
      apiVersion = '3.47';
    }

    const googleMapsScriptSrc = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=${apiVersion}&libraries=geometry&callback=Function.prototype`;
    this._appendScript(googleMapsScriptSrc, () => this._googleMapsLoadedSubject.next(true));
  }

  public loadUtilityLibraries(): void {
    if (
      isPlatformServer(this._platformId) ||
      (this.isGoogleMapsLoaded() && this.isCustomMarkerLoaded() && this.isInfoBoxLoaded() && this.isPositionHighlightOverlayLoaded())
    ) {
      return;
    }

    this._loadGoogleUtilityLibraries();
  }

  public isGoogleMapsLoaded(): boolean {
    if (isPlatformServer(this._platformId)) {
      return false;
    }

    return !isElementNull((window as IGoogleWindow).google) && !isElementNull((window as IGoogleWindow).google.maps);
  }

  public isCustomMarkerLoaded(): boolean {
    if (isPlatformServer(this._platformId)) {
      return false;
    }

    return !isElementNull((window as ICustomMarkerWindow).CustomMarker);
  }

  public isPositionHighlightOverlayLoaded(): boolean {
    if (isPlatformServer(this._platformId)) {
      return false;
    }

    return !isElementNull((window as IPositionHighlightOverlayWindow).PositionHighlightOverlay);
  }

  public isInfoBoxLoaded(): boolean {
    if (isPlatformServer(this._platformId)) {
      return false;
    }

    return !isElementNull((window as IInfoBoxWindow).InfoBox);
  }

  private _onCustomMarkerLoaded(): void {
    this._customMarkerLoadedSubject.next(true);
  }

  private _onPositionHighlightOverlayLoaded(): void {
    this._positionHighlightOverlayLoadedSubject.next(true);
  }

  private _onInfoboxLoaded(): void {
    this._infoBoxLoadedSubject.next(true);
  }

  private _loadGoogleUtilityLibraries(): void {
    this._customMarkerLoaderService
      .load()
      .pipe(
        filter(loaded => !isElementNull(loaded)),
        take(1)
      )
      .subscribe(loaded => {
        (window as ICustomMarkerWindow).CustomMarker = loaded;
        this._onCustomMarkerLoaded();
      });

    const positionHighlightOverlayScriptSrc = `./position-highlight-overlay.js?${environment.buildTimestamp}`;
    this._appendScript(positionHighlightOverlayScriptSrc, () => this._onPositionHighlightOverlayLoaded());

    const infoBoxScriptSrc = `./infobox.js?${environment.buildTimestamp}`;
    this._appendScript(infoBoxScriptSrc, () => this._onInfoboxLoaded());
  }

  private _createScriptElement(scriptSrc: string, onLoadFunc: (this: GlobalEventHandlers, event: Event) => void): HTMLScriptElement {
    const script = this._document.createElement('script');

    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = scriptSrc;
    script.onload = onLoadFunc;

    return script;
  }

  private _appendScript(scriptSrc: string, onLoadFunc: (this: GlobalEventHandlers, event: Event) => void): void {
    const script = this._createScriptElement(scriptSrc, onLoadFunc);
    this._document.body.appendChild(script);
  }
}
